import React from "react";
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Header3, Select, SelectTrigger } from "@activecollab/components";
import { useFormikContext } from "formik";
import PropTypes from "prop-types";
import { CustomFormField } from "~/components/CustomFormField";
import { FormCheckbox } from "~/components/ModalObjects";
import { longMonthNames } from "~/utils/date";
import weekDays from "~/utils/weekDays";

const MONTH_GROUPS = {
  bimonthly: [
    { id: 1, name: "Jan / Mar / May / Jul / Sep / Nov" },
    { id: 2, name: "Feb / Apr / Jun / Avg / Oct / Dec" },
  ],
  quarterly: [
    { id: 1, name: "Jan / Apr / Jul / Oct" },
    { id: 2, name: "Feb / May / Aug / Nov" },
    { id: 3, name: "Mar / Jun / Sep / Dec" },
  ],
  semiyearly: [
    { id: 1, name: "Jan / Jul" },
    { id: 2, name: "Feb / Aug" },
    { id: 3, name: "Mar / Sep" },
    { id: 4, name: "Apr / Oct" },
    { id: 5, name: "May / Nov" },
    { id: 6, name: "Jun / Dec" },
  ],
};

const REPEAT_FREQUENCIES = {
  never: "Never",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  bimonthly: "Bi-monthly",
  quarterly: "Quarterly",
  semiyearly: "Semi-yearly",
  yearly: "Yearly",
};

const BaseSelect = ({ field, options, label, triggerWidth = 120, onChange }) => {
  return (
    <Select
      disableSearch
      disabledInternalSort
      keepSameOptionsOrder
      forceCloseMenu
      selected={field.value}
      options={options}
      target={<SelectTrigger className={`tw-w-[${triggerWidth}px]`}>{label}</SelectTrigger>}
      onChange={onChange ? onChange : field.onChange}
    />
  );
};

BaseSelect.propTypes = {
  field: PropTypes.object,
  options: PropTypes.array,
  label: PropTypes.string,
  triggerWidth: PropTypes.number,
  onChange: PropTypes.func,
};

export const RecurrenceSelect = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const weeklyRepeatAmountExtended = [
    { id: 0, name: t("Every week") },
    { id: 1, name: t("Odd week") },
    { id: 2, name: t("Even week") },
  ];

  const repeatFrequencyOptions = useMemo(
    () =>
      Object.entries(REPEAT_FREQUENCIES).map(([id, labelKey]) => ({
        id,
        name: t(labelKey),
      })),
    [t]
  );
  const monthlyRepeatAmounts = useMemo(() => {
    return Array(29)
      .fill(0)
      .map((v, i) => {
        return {
          id: ++i,
          name: `${i === 29 ? "Last" : `${i}.`} ${t("day")}`,
        };
      });
  }, [t]);

  const resolvedOptions = useMemo(() => {
    const months = longMonthNames.map((name, index) => ({
      id: index + 1,
      name,
    }));
    return MONTH_GROUPS[values.repeat_frequency] || months;
  }, [values?.repeat_frequency]);

  const isWeekly = useMemo(() => values.repeat_frequency === "weekly", [values.repeat_frequency]);
  const isMonthly = useMemo(() => values.repeat_frequency === "monthly", [values.repeat_frequency]);
  const isYearBased = useMemo(
    () => ["bimonthly", "quarterly", "semiyearly", "yearly"].includes(values.repeat_frequency),
    [values.repeat_frequency]
  );

  const weekDaysOptions = useMemo(
    () =>
      weekDays.map((name, index) => ({
        id: index + 1,
        name,
      })),
    []
  );

  const resolveSelectLabel = (options, id) => options.find((o) => o.id === id)?.name;

  const handleFrequencyChange = useCallback(
    (val) => {
      const defaultRepeatValues = {
        daily: { amount: 0, extended: 1 },
        weekly: { amount: 1, extended: 0 },
      };
      const defaults = defaultRepeatValues[val] || { amount: 1, extended: 1 };
      setFieldValue("repeat_frequency", val);
      setFieldValue("repeat_amount", defaults.amount);
      setFieldValue("repeat_amount_extended", defaults.extended);
    },
    [setFieldValue]
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-pb-6">
      <Header3>{t("Recurrence Period")}</Header3>
      <div className="tw-flex tw-items-center tw-gap-2">
        <CustomFormField name="repeat_frequency">
          {({ field }) => (
            <BaseSelect
              field={field}
              options={repeatFrequencyOptions}
              label={REPEAT_FREQUENCIES[field.value]}
              triggerWidth={140}
              onChange={handleFrequencyChange}
            />
          )}
        </CustomFormField>
        {values.repeat_frequency === "never" ? "Recurrence is off" : null}
        {values.repeat_frequency === "daily" ? (
          <CustomFormField name="repeat_amount">
            {({ field }) => (
              <FormCheckbox
                id="repeat_amount"
                checked={!field.value}
                onChange={() => setFieldValue(field.name, !field.value)}
                className="form_field tw-my-2"
                text={t("Only Workdays")}
              />
            )}
          </CustomFormField>
        ) : null}
        {isWeekly ? (
          <>
            <Body2 whitespace="no-wrap">{t("every")}</Body2>
            <CustomFormField name="repeat_amount">
              {({ field }) => (
                <BaseSelect
                  field={field}
                  options={weekDaysOptions}
                  label={weekDays[values.repeat_amount - 1]}
                />
              )}
            </CustomFormField>
            <Body2 whitespace="no-wrap">{t("of the")}</Body2>
            <CustomFormField name="repeat_amount_extended">
              {({ field }) => (
                <BaseSelect
                  field={field}
                  options={weeklyRepeatAmountExtended}
                  label={resolveSelectLabel(weeklyRepeatAmountExtended, field.value)}
                  triggerWidth={184}
                />
              )}
            </CustomFormField>
          </>
        ) : null}
        {isMonthly ? (
          <>
            <Body2 whitespace="no-wrap">{t("on the")}</Body2>
            <CustomFormField name="repeat_amount">
              {({ field }) => (
                <BaseSelect
                  field={field}
                  options={monthlyRepeatAmounts}
                  label={resolveSelectLabel(monthlyRepeatAmounts, field.value)}
                />
              )}
            </CustomFormField>
            <Body2 whitespace="no-wrap">{t("of the month")}</Body2>
          </>
        ) : null}
        {isYearBased ? (
          <>
            <Body2 whitespace="no-wrap">{t("on the")}</Body2>
            <CustomFormField name="repeat_amount_extended">
              {({ field }) => (
                <BaseSelect
                  field={field}
                  options={monthlyRepeatAmounts}
                  label={resolveSelectLabel(monthlyRepeatAmounts, field.value)}
                />
              )}
            </CustomFormField>
            <Body2 whitespace="no-wrap">{t("of")}</Body2>
            <CustomFormField name="repeat_amount">
              {({ field }) => (
                <BaseSelect
                  field={field}
                  options={resolvedOptions}
                  label={resolveSelectLabel(resolvedOptions, field.value)}
                  triggerWidth={184}
                />
              )}
            </CustomFormField>
          </>
        ) : null}
      </div>
    </div>
  );
};
